import React, { useState, useEffect } from 'react';
import { hasPermission } from '@apply/ui';
import { PermissionType } from '@apply/constants';
import DataGrid, { Column, Paging, Pager } from 'devextreme-react/data-grid';
import { findArrayIndexByPropertyName, formatDateYearMonth } from '../../../common/SharedFunctions';
import { InformModal } from '../../../common/SharedComponents';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faPencil, faTrash } from '@fortawesome/free-solid-svg-icons';
import { NoteDetailDialog } from './NoteDetailDialog';
import { WatchlistDetailNoteType, WatchlistDetailNoteTypeOptions } from '../WatchlistConstants';

export const NotesGrid = ({ notes, noteType = WatchlistDetailNoteType.Loan, isLocked = false, onChanged, setDeleteNote }) => {
    const [noteToEdit, set_noteToEdit] = useState(null);
    const [notesArray, set_notesArray] = useState([]);
    const [informModalOpen, set_informModalOpen] = useState(false);
    const [informModalMessage, set_informModalMessage] = useState('');
    const [noteTypeDisplay, set_noteTypeDisplay] = useState('');

    if (!notes || !noteType) {
        return null;
    }

    useEffect(() => {
        if (!notes) {
            return;
        }

        const noteData = notes.map((obj, index) => ({
            ...obj,
            virtualId: index + 1, // Adding a new property 'virtualId' to each object - used only for interface modification purposes
        }));
        set_notesArray(noteData);
    }, [notes]);

    useEffect(() => {
        if (!noteType) {
            return;
        }
        const displayName = WatchlistDetailNoteTypeOptions.find((f) => f.key === noteType)?.display;
        if (!displayName) {
            set_noteTypeDisplay('');
        } else {
            set_noteTypeDisplay(displayName);
        }
    }, [noteType]);

    const canEdit = () => {
        return !isLocked && hasPermission(PermissionType.WatchlistEdit);
    };

    const onCellDblClick = (e) => {
        const note = notesArray.find((a) => a.startDate === e.data.startDate);
        if (!note) {
            return;
        }
        set_noteToEdit(note);
    };

    const addNewNote = () => {
        const model = {
            startDate: null,
            notes: '',
        };
        set_noteToEdit(model);
    };

    const onMonthRender = (e) => {
        if (!e.value) {
            return;
        }
        return formatDateYearMonth(e.value);
    };

    const onDeleteNote = (e) => {
        const array = [...notesArray];
        const note = array.find((a) => a.startDate === e.data.startDate);
        if (note < 0) {
            return;
        }
        note.isDeleted = !note.isDeleted;
        set_notesArray(array);

        onChanged && onChanged([...array]);
        if (note.isDeleted) {
            setDeleteNote((o) => o + 1);
        } else {
            setDeleteNote((o) => o - 1);
        }
    };

    const onNotesActionsRender = (e) => {
        return (
            <>
                <button
                    type='button'
                    id='editNote'
                    className='gridButton'
                    title={!canEdit() ? 'View Note' : 'Edit Note'}
                    onClick={(event) => onCellDblClick(e)}
                >
                    <FontAwesomeIcon icon={!canEdit() ? faEye : faPencil} />
                </button>
                &nbsp;
                <button
                    type='button'
                    id='deleteNote'
                    className='gridButton'
                    title={e.data.isDeleted ? 'Will be deleted on save - Click to undelete' : 'Delete'}
                    disabled={!canEdit()}
                    onClick={(event) => onDeleteNote(e)}
                >
                    <FontAwesomeIcon
                        icon={faTrash}
                        style={e.data.isDeleted ? { color: 'red' } : { color: '#2c5591' }}
                    />
                </button>
            </>
        );
    };

    const onNoteModalSubmit = (note) => {
        let array = [...notesArray];

        let index = findArrayIndexByPropertyName(array, 'virtualId', note.virtualId);
        let potentialDuplicateNotes = array.filter((x) => x.startDate === note.startDate && x.virtualId !== note.virtualId);

        if (potentialDuplicateNotes.length > 0) {
            set_informModalMessage('Unable to Save: A seperate note already exists for this date.');
            set_informModalOpen(true);
            return false;
        }

        if (~index) {
            array[index].startDate = note.startDate;
            array[index].notes = note.notes;
        } else {
            array.push(note);
        }

        set_notesArray(array);
        set_noteToEdit(null);

        onChanged && onChanged([...array]);
    };

    const onInformModalConfirm = () => {
        set_informModalOpen(false);
    };

    const onNoteModalCancel = () => {
        set_noteToEdit(null);
    };

    if (!notesArray) {
        return null;
    }

    return (
        <>
            <DataGrid
                dataSource={notesArray}
                rowAlternationEnabled={true}
                noDataText={noteTypeDisplay ? `No ${noteTypeDisplay} Notes Saved` : `No Notes Saved`}
                showBorders={true}
                showRowLines={false}
                remoteOperations={false}
                allowColumnReordering={false}
                allowColumnResizing={true}
                onCellDblClick={onCellDblClick}
            >
                <Column
                    dataField='startDate'
                    caption='Month'
                    cellRender={onMonthRender}
                    width='70'
                    sortIndex={0}
                    sortOrder={'desc'}
                    allowSorting={false}
                    alignment={'left'}
                />
                <Column
                    dataField='notes'
                    caption='Notes'
                    alignment='left'
                    allowSorting={false}
                    cssClass='notesColumn'
                />
                <Column
                    alignment='center'
                    cellRender={onNotesActionsRender}
                    width={80}
                    allowSorting={false}
                    allowResizing={false}
                />
                <Paging defaultPageSize={10} />
                <Pager
                    showPageSizeSelector={true}
                    allowedPageSizes={[10, 20, 30]}
                    showNavigationButtons={true}
                    showInfo={true}
                />
            </DataGrid>
            <div style={{ marginTop: '5px' }}>
                <button
                    type='button'
                    className='button primary'
                    title={noteTypeDisplay ? `Add a new ${noteTypeDisplay} Note` : `Add a new Note`}
                    onClick={addNewNote}
                    disabled={!canEdit()}
                >
                    Add {noteTypeDisplay} Note
                </button>
            </div>
            <InformModal
                isOpen={informModalOpen}
                text={informModalMessage}
                confirmCallback={onInformModalConfirm}
            />
            <NoteDetailDialog
                noteType={noteType}
                note={noteToEdit}
                onCancel={onNoteModalCancel}
                onSubmit={onNoteModalSubmit}
            />
        </>
    );
};
